import React, { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, X } from "lucide-react";
import Select from "react-select";
import api from "../../../api/axios";
import { ErrorMessage, validate } from "../../../components/validation/Validation";
import JSON5 from 'json5';

const rules = {
  template_name: {
    required: true,
    minLength: 3,
    maxLength: 100,
    label: "Template Name",
  },

  template_type: {
    required: true,
    label: "Template Type",
  },

  template_layout: {
    required: true,
    label: "Template Layout",
  },

  template_video_url: {
    pattern: /^(https?:\/\/)([\w.-]+)(:[0-9]+)?(\/.*)?$/i,
    label: "Video URL",
    message: "Please enter a valid URL.",
  },

  template_tags: {
    required: true,
    label: "Tags",
  },

  currentTime: {
    required: true,
    type: "number",
    validator: (value) => {
      if (Number(value) < 0) {
        return "Preview Time cannot be negative.";
      }
    },
    label: "Preview Time",
  },

  template_status: {
    required: true,
    label: "Status",
  },

  // New validation rule for defaultValues
  defaultValues: {
    required: true,
    label: "Default Values (JSON)",
    validator: (value) => {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed !== "object" || Array.isArray(parsed)) {
          return "Default Values must be a JSON object.";
        }
        return null;
      } catch (e) {
        return "Invalid JSON format. Please enter a valid JSON object.";
      }
    },
  },
};

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    console.log("TemplatesPage mounted");
    getTemplates();
  }, []);

  const getTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get("/template/list");
      if (response && response.data && response.data.success) {
        setTemplates(response.data?.data?.list || []);
      } else {
        setLoading(false);
        setTemplates([]);
      }
    } catch (err) {
      setLoading(false);
      console.log(err?.data?.message);
    }
  };

  const rows = useMemo(
    () =>
      templates.filter((item) => {
        const keyword = search.toLowerCase();
        return (
          item.template_name?.toLowerCase().includes(keyword) ||
          item.template_type?.toLowerCase().includes(keyword) ||
          item.template_layout?.toLowerCase().includes(keyword)
        );
      }),
    [templates, search]
  );

  const saveTemplate = async (event, selectedTypes, defaultValues) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const values = {
      ...Object.fromEntries(new FormData(event.currentTarget)),
      template_type: selectedTypes.map((x) => x.value),
      defaultValues: defaultValues, // add to validate object
    };

    const validationErrors = validate(values, rules);

    if (selectedTypes.length === 0) {
      validationErrors.template_type = "Please select at least one template type.";
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const payload = {
      template_name: formData.get("template_name"),
      template_layout: formData.get("template_layout"),
      template_video_url: formData.get("template_video_url"),
      template_status: formData.get("template_status"),

      template_type: selectedTypes.map((item) => item.value),

      template_tags: formData
        .get("template_tags")
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),

      template_content: {
        currentTime: Number(formData.get("currentTime")),
        defaultValues: JSON5.parse(defaultValues), // parse JSON
      },
    };

    try {
      setLoading(true);

      let response;
      if (form?.template_id) {
        response = await api.put(`/template/${form?.template_id}`, payload);
      } else {
        response = await api.post("/template/save", payload);
      }

      if (response && response.data && response.data.success) {
        setForm(null);
        getTemplates();
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      console.log(err?.data?.message);
    }
  };

  const handleValidationFormChange = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => {
      if (!prev[name]) return prev;

      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <div>
          <h2 className="text-2xl font-bold">Templates</h2>
        </div>
        <button
          onClick={() => setForm({})}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          <Plus size={17} />
          Create Template
        </button>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* Search */}
        <div className="border-b border-slate-100 p-4">
          <label className="relative block">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates…"
              className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm"
            />
          </label>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {[
                  "Preview",
                  "Template",
                  "Type",
                  "Layout",
                  "Status",
                  "Created",
                  "Updated",
                  "Actions",
                ].map((x) => (
                  <th key={x} className="px-5 py-3">
                    {x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((item) => (
                <tr key={item._id} className="hover:bg-slate-50">
                  {/* Preview */}
                  <td className="px-5 py-4">
                    <video
                      src={item.template_video_url}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-20 w-32 rounded-lg border object-cover"
                      onLoadedMetadata={(e) => {
                        e.target.currentTime = item.template_content?.currentTime || 2;
                      }}
                      onSeeked={(e) => {
                        e.target.pause();
                      }}
                    />
                  </td>

                  {/* Name */}
                  <td className="px-5 py-4 font-semibold">
                    {item.template_name}
                  </td>

                  {/* Type */}
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-2">
                      {(Array.isArray(item.template_type)
                        ? item.template_type
                        : item.template_type
                          ? [item.template_type]
                          : []
                      ).map((type) => (
                        <span
                          key={type}
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${{
                            FREE: "bg-green-100 text-green-700",
                            STARTER: "bg-blue-100 text-blue-700",
                            PRO: "bg-violet-100 text-violet-700",
                            ENTERPRISE: "bg-amber-100 text-amber-700",
                          }[type] || "bg-slate-100 text-slate-700"
                            }`}
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Layout */}
                  <td className="px-5 py-4">{item.template_layout}</td>

                  {/* Status */}
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${item.template_status === "ACTIVE"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                        }`}
                    >
                      {item.template_status}
                    </span>
                  </td>

                  {/* Created */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    {new Date(item.template_created_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    <div className="text-xs text-slate-500">
                      {new Date(item.template_created_on).toLocaleTimeString("en-IN")}
                    </div>
                  </td>

                  {/* Updated */}
                  <td className="px-5 py-4 whitespace-nowrap">
                    {new Date(item.template_updated_on).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    <div className="text-xs text-slate-500">
                      {new Date(item.template_updated_on).toLocaleTimeString("en-IN")}
                    </div>
                  </td>

                  {/* Action */}
                  <td className="px-5 py-4">
                    <button
                      onClick={() => setForm(item)}
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Pencil size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {form && (
        <TemplateForm
          value={form}
          onClose={() => setForm(null)}
          onSave={saveTemplate}
          errors={errors}
          setErrors={setErrors}
          handleFormChange={handleValidationFormChange}
        />
      )}
    </div>
  );
}

// ---------- Template Form ----------
function TemplateForm({
  value = {},
  onClose,
  onSave,
  setErrors,
  errors,
  handleFormChange,
}) {
  const templateTypeOptions = [
    { value: "FREE", label: "FREE" },
    { value: "STARTER", label: "STARTER" },
    { value: "PRO", label: "PRO" },
    { value: "ENTERPRISE", label: "ENTERPRISE" },
  ];

  const [selectedTypes, setSelectedTypes] = useState(() => {
    const types = Array.isArray(value?.template_type)
      ? value.template_type
      : value?.template_type
        ? [value.template_type]
        : [];

    return types.map((type) => ({
      value: type,
      label: type,
    }));
  });

  // State for JSON default values
  const [defaultValues, setDefaultValues] = useState(() => {
    const dv = value?.template_content?.defaultValues || {};
    try {
      return JSON.stringify(dv, null, 2);
    } catch {
      return "{}";
    }
  });

  // Update defaultValues when `value` changes (e.g., edit existing)
  useEffect(() => {
    const dv = value?.template_content?.defaultValues || {};
    try {
      setDefaultValues(JSON.stringify(dv, null, 2));
    } catch {
      setDefaultValues("{}");
    }
  }, [value]);

  const handleDefaultValuesChange = (e) => {
    const val = e.target.value;
    setDefaultValues(val);
    // Clear error on change
    setErrors((prev) => {
      if (!prev.defaultValues) return prev;
      const newErrors = { ...prev };
      delete newErrors.defaultValues;
      return newErrors;
    });
  };

  const formatJSON = () => {
    try {
      // Parse with JSON5 (accepts relaxed syntax)
      const parsed = JSON5.parse(defaultValues);
      // Stringify with proper JSON (double quotes, no trailing commas)
      setDefaultValues(JSON.stringify(parsed, null, 2));

      // Clear any error
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.defaultValues;
        return newErrors;
      });
    } catch (e) {
      alert("Invalid JSON – please check your syntax.\nError: " + e.message);
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 46,
      borderRadius: 10,
      borderColor: "#CBD5E1",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#6366F1",
      },
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: "#EEF2FF",
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: "#4338CA",
      fontWeight: 500,
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: "#4338CA",
      ":hover": {
        backgroundColor: "#C7D2FE",
        color: "#312E81",
      },
    }),
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 h-full !mt-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(e, selectedTypes, defaultValues);
        }}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-semibold">
            {value._id ? "Edit Template" : "Create Template"}
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {/* Template Name */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              name="template_name"
              defaultValue={value.template_name || ""}
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
            />
            <ErrorMessage error={errors.template_name} />
          </div>

          {/* Type & Layout */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Template Type <span className="text-red-500">*</span>
              </label>
              <Select
                isMulti
                options={templateTypeOptions}
                value={selectedTypes}
                onChange={setSelectedTypes}
                styles={selectStyles}
                placeholder="Select template types..."
                closeMenuOnSelect={false}
                className="text-sm"
                classNamePrefix="react-select"
              />
              <ErrorMessage error={errors.template_type} />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Template Layout <span className="text-red-500">*</span>
              </label>
              <input
                name="template_layout"
                defaultValue={value.template_layout || ""}
                placeholder="layout1"
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
              />
              <ErrorMessage error={errors.template_layout} />
            </div>
          </div>

          {/* Video URL */}
          <div>
            <label className="mb-2 block text-sm font-medium">Video URL</label>
            <input
              type="url"
              name="template_video_url"
              defaultValue={value.template_video_url || ""}
              placeholder="https://..."
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
            />
            <ErrorMessage error={errors.template_video_url} />
          </div>

          {/* Tags */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Tags <span className="text-red-500">*</span>
            </label>
            <input
              name="template_tags"
              defaultValue={(value.template_tags || []).join(", ")}
              placeholder="Best for eCommerce, Premium..."
              onChange={handleFormChange}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
            />
            <ErrorMessage error={errors.template_tags} />
            <p className="mt-1 text-xs text-slate-500">
              Separate multiple tags with commas.
            </p>
          </div>

          {/* Current Time + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium">
                Preview Time (sec) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                name="currentTime"
                defaultValue={value.template_content?.currentTime || 2}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
              />
              <ErrorMessage error={errors.currentTime} />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="template_status"
                defaultValue={value.template_status || "ACTIVE"}
                onChange={handleFormChange}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
              <ErrorMessage error={errors.template_status} />
            </div>
          </div>

          {/* NEW: Default Values (JSON) */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              Default Values (JSON) <span className="text-red-500">*</span>
            </label>
            <div className="flex justify-end mb-1">
              <button
                type="button"
                onClick={formatJSON}
                className="text-xs text-indigo-600 hover:text-indigo-800"
              >
                Format JSON
              </button>
            </div>
            <textarea
              name="defaultValues"
              value={defaultValues}
              onChange={handleDefaultValuesChange}
              rows={6}
              className={`w-full rounded-lg border font-mono text-sm px-3 py-2.5 ${errors.defaultValues ? "border-red-500" : "border-slate-300"
                }`}
              placeholder={`{\n  "camp_name": "",\n  "brand_name": "Example",\n  ...\n}`}
            />
            <ErrorMessage error={errors.defaultValues} />
            <p className="mt-1 text-xs text-slate-500">
              Must be a valid JSON object. This will be used as the template's default data.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border px-5 py-2.5"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="rounded-lg bg-indigo-600 px-5 py-2.5 font-medium text-white hover:bg-indigo-700"
          >
            {value._id ? "Update Template" : "Create Template"}
          </button>
        </div>
      </form>
    </div>
  );
}